import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Plus, X, Pencil, Trash2 } from 'lucide-react';
import { useToast } from './ui/use-toast';
import { useIsMobile } from '../hooks/use-mobile';
import { getBaseUrl } from '@/lib/api';

type Representative = {
  Id: number;
  RepName: string;
  RepID?: number;
  RepType?: string;
  Tag1?: string;
  Tag2?: string;
  Location?: string;
};

type LocationItem = {
  Id?: number | string;
  Location?: string;
  Name?: string;
};

interface RepresentativeManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAnyChange?: () => void; // callback to let parent refresh representatives if needed
}

const REP_TYPES = [
  'Day full time',
  'Day contractor',
  'Night full time',
  'Night contractor',
];

const getAuthToken = () => {
  const token = localStorage.getItem('token') || '';
  return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
};

const RepresentativeManager: React.FC<RepresentativeManagerProps> = ({ open, onOpenChange, onAnyChange }) => {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [locations, setLocations] = useState<LocationItem[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [representatives, setRepresentatives] = useState<Representative[]>([]);
  const [repsLoading, setRepsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState<'add' | 'update'>('add');
  const [formData, setFormData] = useState<Representative>({ Id: 0, RepName: '', RepID: undefined, RepType: REP_TYPES[0], Tag1: '', Tag2: '', Location: '' });
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const isMobile = useIsMobile();
  const [expandedCardIds, setExpandedCardIds] = useState<Set<number>>(new Set());

  const toggleCard = (id: number) => {
    setExpandedCardIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const defaultLocation = useMemo(() => {
    if (!locations || locations.length === 0) return '';
    // Find common field name (supports lowercase 'location')
    const first = locations[0] as any;
    return first?.Location || first?.location || first?.Name || '';
  }, [locations]);

  useEffect(() => {
    if (open) {
      // Load initial data
      fetchLocations();
      fetchRepresentatives();
    }
  }, [open]);

  const fetchLocations = useCallback(async () => {
    setLocationsLoading(true);
    try {
      const response = await fetch(`${getBaseUrl()}/api/Locations`, {
        headers: { 'Authorization': getAuthToken(), 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      const items: LocationItem[] = data?.data || data?.Data || data || [];
      setLocations(items);
      // Ensure default selection for form when opening add mode
      setFormData(prev => ({
        ...prev,
        Location: ((items?.[0] as any)?.Location) || ((items?.[0] as any)?.location) || ((items?.[0] as any)?.Name) || ''
      }));
    } catch (e) {
      setLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  }, []);

  const fetchRepresentatives = useCallback(async () => {
    setRepsLoading(true);
    try {
      const response = await fetch(`${getBaseUrl()}/api/Representative`, {
        headers: { 'Authorization': getAuthToken(), 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      const reps: Representative[] = (data?.data || data?.Data || data || []).map((r: any) => ({
        Id: r.Id,
        RepName: r.RepName ?? r.Representative ?? '',
        RepID: r.RepID,
        RepType: r.RepType,
        Tag1: r.Tag1,
        Tag2: r.Tag2,
        // Support both 'Location' and 'location' from API
        Location: r.Location ?? r.location ?? '',
      }));
      setRepresentatives(reps);
    } catch (e) {
      setRepresentatives([]);
    } finally {
      setRepsLoading(false);
    }
  }, []);

  const resetForm = () => {
    setFormData({ Id: 0, RepName: '', RepID: undefined, RepType: REP_TYPES[0], Tag1: '', Tag2: '', Location: defaultLocation || '' });
  };

  const openAddForm = () => {
    setFormMode('add');
    resetForm();
    setShowForm(true);
  };

  const openUpdateForm = (rep: Representative) => {
    setFormMode('update');
    setFormData({ ...rep, RepType: rep.RepType || REP_TYPES[0], Location: rep.Location || defaultLocation || '' });
    setShowForm(true);
  };

  const handleAddOrUpdate = async () => {
    if (!formData.RepName || formData.RepName.trim() === '') {
      toast({ title: 'Validation', description: 'Rep Name is mandatory', variant: 'destructive' });
      return;
    }

    try {
      const payload = {
        Id: formMode === 'update' ? formData.Id : undefined,
        RepName: formData.RepName,
        RepID: formData.RepID ?? 0,
        RepType: formData.RepType,
        Tag1: formData.Tag1,
        Tag2: formData.Tag2,
        Location: formData.Location || defaultLocation || '',
      } as any;

      const method = formMode === 'add' ? 'POST' : 'PUT';
      const response = await fetch(`${getBaseUrl()}/api/Representative`, {
        method,
        headers: { 'Authorization': getAuthToken(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data?.message || data?.Message || 'Request failed');

      const apiMessage = (data?.message || data?.Message || '').toString();
      if (apiMessage.trim().toLowerCase() === 'success') {
        toast({
          title: formMode === 'add' ? 'Representative Added Successfully' : 'Representative Updated Successfully',
        });
      } else {
        toast({
          description: apiMessage || undefined,
        });
      }

      setShowForm(false);
      await fetchRepresentatives();
      if (onAnyChange) onAnyChange();
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message || 'Operation failed', variant: 'destructive' });
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      const response = await fetch(`${getBaseUrl()}/api/Representative`, {
        method: 'DELETE',
        headers: { 'Authorization': getAuthToken(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ Id: confirmDeleteId }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || data?.Message || 'Delete failed');
      const apiMessage = (data?.message || data?.Message || '').toString();
      if (apiMessage.trim().toLowerCase() === 'success') {
        toast({ title: 'Representative Deleted Successfully' });
      } else {
        toast({ description: apiMessage || undefined });
      }
      setConfirmDeleteId(null);
      await fetchRepresentatives();
      if (onAnyChange) onAnyChange();
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message || 'Delete failed', variant: 'destructive' });
    }
  };

  const filteredReps = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return representatives;
    return representatives.filter(r =>
      (r.RepName || '').toLowerCase().includes(term) ||
      (r.RepType || '').toLowerCase().includes(term) ||
      (r.Tag1 || '').toLowerCase().includes(term) ||
      (r.Tag2 || '').toLowerCase().includes(term) ||
      (r.Location || '').toLowerCase().includes(term)
    );
  }, [search, representatives]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Representatives</DialogTitle>
          <DialogDescription>Search, add, update, or delete representatives.</DialogDescription>
        </DialogHeader>

        {/* Top controls: search with clear, and + button */}
        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Input
              placeholder="Search representatives"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                onClick={() => setSearch('')}
                aria-label="Clear search"
              >
                <X size={18} />
              </button>
            )}
          </div>
          <Button type="button" onClick={openAddForm} className="whitespace-nowrap">
            <Plus className="mr-1" size={16} /> New
          </Button>
        </div>

        {/* Representatives list */}
        {isMobile ? (
          <div className="space-y-3">
            {repsLoading ? (
              <div className="text-center py-6">Loading...</div>
            ) : filteredReps.length === 0 ? (
              <div className="text-center py-6">No representatives found</div>
            ) : (
              filteredReps.map((rep) => (
                <div key={rep.Id} className="border rounded-md p-2 shadow-sm bg-white">
                  {/* Top row: Rep Name + Location on same line */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[13px] truncate">{rep.RepName}</div>
                    </div>
                    <div className="text-[11px] text-gray-600 truncate max-w-[55%] text-right">
                      {rep.Location || '-'}
                    </div>
                  </div>

                  {/* Rep type under header */}
                  <div className="text-[11px] text-gray-700 mt-1">{rep.RepType}</div>

                  {/* Collapsible extra fields */}
                  {expandedCardIds.has(rep.Id) && (
                    <div className="grid grid-cols-2 gap-2 mt-2 text-[11px]">
                      <div>
                        <div className="text-gray-500">Tag1</div>
                        <div className="text-gray-800">{rep.Tag1 || '-'}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Tag2</div>
                        <div className="text-gray-800">{rep.Tag2 || '-'}</div>
                      </div>
                    </div>
                  )}

                  {/* Bottom row: More/Less and actions */}
                  <div className="mt-2 flex items-center justify-between">
                    <button
                      type="button"
                      className="text-cyan-700 text-[11px] font-medium"
                      onClick={() => toggleCard(rep.Id)}
                    >
                      {expandedCardIds.has(rep.Id) ? 'Less' : 'More'}
                    </button>
                    <div className="flex gap-2">
                      <Button variant="outline" size="icon" onClick={() => openUpdateForm(rep)} aria-label="Update">
                        <Pencil size={16} />
                      </Button>
                      <Button variant="destructive" size="icon" onClick={() => setConfirmDeleteId(rep.Id)} aria-label="Delete">
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rep Name</TableHead>
                  <TableHead>Rep Type</TableHead>
                  <TableHead>Tag1</TableHead>
                  <TableHead>Tag2</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {repsLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-6">Loading...</TableCell>
                  </TableRow>
                ) : filteredReps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-6">No representatives found</TableCell>
                  </TableRow>
                ) : (
                  filteredReps.map((rep) => (
                    <TableRow key={rep.Id}>
                      <TableCell>{rep.RepName}</TableCell>
                      <TableCell>{rep.RepType}</TableCell>
                      <TableCell>{rep.Tag1}</TableCell>
                      <TableCell>{rep.Tag2}</TableCell>
                      <TableCell>{rep.Location}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="icon" onClick={() => openUpdateForm(rep)} aria-label="Update">
                            <Pencil size={16} />
                          </Button>
                          <Button variant="destructive" size="icon" onClick={() => setConfirmDeleteId(rep.Id)} aria-label="Delete">
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Add/Update form modal content within dialog */}
        {showForm && (
          <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-[100]">
            <div className="bg-white w-[95vw] max-w-[520px] rounded-lg shadow-lg p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-lg font-semibold">{formMode === 'add' ? 'Add Representative' : 'Update Representative'}</div>
                <button className="text-gray-500 hover:text-gray-700" onClick={() => setShowForm(false)} aria-label="Close">
                  <X size={18} />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Rep Name<span className="text-red-500">*</span></label>
                  <Input
                    value={formData.RepName}
                    onChange={(e) => setFormData(prev => ({ ...prev, RepName: e.target.value }))}
                    placeholder="Enter rep name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Rep Type</label>
                  <Select value={formData.RepType || ''} onValueChange={(val) => setFormData(prev => ({ ...prev, RepType: val }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {REP_TYPES.map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tag1</label>
                  <Input value={formData.Tag1 || ''} onChange={(e) => setFormData(prev => ({ ...prev, Tag1: e.target.value }))} placeholder="Optional" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tag2</label>
                  <Input value={formData.Tag2 || ''} onChange={(e) => setFormData(prev => ({ ...prev, Tag2: e.target.value }))} placeholder="Optional" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium mb-1">Location</label>
                  <Select
                    value={formData.Location || defaultLocation || ''}
                    onValueChange={(val) => setFormData(prev => ({ ...prev, Location: val }))}
                  >
                    <SelectTrigger disabled={locationsLoading}>
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc, idx) => {
                        const anyLoc = loc as any;
                        const name = anyLoc?.Location || anyLoc?.location || anyLoc?.Name || '';
                        return <SelectItem key={idx} value={name}>{name}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setShowForm(false)}>Close</Button>
                <Button onClick={handleAddOrUpdate}>{formMode === 'add' ? 'Add' : 'Update'}</Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirmation inline */}
        {confirmDeleteId !== null && (
          <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-[100]">
            <div className="bg-white w-[90vw] max-w-[420px] rounded-lg shadow-lg p-4 sm:p-6">
              <div className="text-lg font-semibold mb-2">Confirm Delete</div>
              <div className="text-sm mb-4">Are you sure you want to delete?</div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirmDeleteId(null)}>No</Button>
                <Button variant="destructive" onClick={handleConfirmDelete}>Yes</Button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RepresentativeManager;


